import { useMutation, useQueryClient } from "@tanstack/react-query";
import { orderHistoryApi } from "@/features/my-order/features/order-history/api";
import useIdentity from "@/features/ui/hooks/use-identity";
import { companyTypeRequestFormatConverter } from "@/utils/converter";
import { OrderSheetSetInquiriesResponse } from "@/features/my-order/features/order-history/types";
import { AGENCY_INQUIRY_THREADS_QUERY_KEY } from "@/features/inquiries/hooks/useGetAgencyInquiryThreads";

const useMarkOrderSheetSetInquiriesRead = (
  orderSheetSetId?: number | null
) => {
  const queryClient = useQueryClient();
  const identity = useIdentity();
  const companyId = identity?.company?.id;
  const companyType =
    companyTypeRequestFormatConverter[identity?.company?.type ?? "BUYER"];

  // useGetOrderSheetSetInquiries 와 동일한 쿼리 키
  const inquiriesQueryKey = [
    "order-sheet-set",
    "inquiries",
    companyType,
    companyId,
    orderSheetSetId,
  ];

  const { mutate: markRead, mutateAsync: markReadAsync } = useMutation({
    mutationKey: [
      "order-sheet-set",
      "inquiries",
      "read",
      companyType,
      companyId,
      orderSheetSetId,
    ],
    mutationFn: () => {
      if (!orderSheetSetId || !companyId) {
        return Promise.resolve(null);
      }
      return orderHistoryApi.markOrderSheetSetInquiriesRead({
        companyType,
        companyId,
        orderSheetSetId,
      });
    },
    // 읽음 처리 즉시 unread 배지를 0으로. is_unread(메시지 강조)는 유지해 이번 화면에선 어떤 게 새 글이었는지 보이도록 둔다.
    onMutate: () => {
      queryClient.setQueryData<OrderSheetSetInquiriesResponse>(
        inquiriesQueryKey,
        (old) => (old ? { ...old, unread_count: 0 } : old)
      );
    },
    // 에이전시 문의함 메뉴 배지·목록의 안 읽음 수를 다시 불러온다
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: AGENCY_INQUIRY_THREADS_QUERY_KEY,
      });
    },
  });

  return { markRead, markReadAsync };
};

export default useMarkOrderSheetSetInquiriesRead;
